import { defineStore } from 'pinia'
import type { Region } from '@storyblok/region-helper'
import type { AxiosResponse } from 'axios'
import config from '@/config'
import TRIAL_DETAILS from '@constants/trialDays'
import { client } from '@/services/client'
import { handleRequest } from '@/services/handleRequest'
import { useSpace } from '@/store/modules/App/space'
import type { Tag, LimitedSpace, Collaborator } from '@/types'
import { t } from '@/utils/helpers/translate'

export type PlanExpiration = {
  expiredPlanThreshold: number
  expiringSoonPlanThreshold: number
  trialPeriodDuration: number
}
export type State = {
  tags: Tag[]
  euSpaces: LimitedSpace[]
  usSpaces: LimitedSpace[]
  cnSpaces: LimitedSpace[]
  caSpaces: LimitedSpace[]
  apSpaces: LimitedSpace[]
  planExpirationDays: PlanExpiration
}

export type SpacesStore = ReturnType<typeof useSpaces>

export const useSpaces = defineStore('spaces', {
  state: (): State => ({
    tags: [],
    euSpaces: [],
    usSpaces: [],
    cnSpaces: [],
    caSpaces: [],
    apSpaces: [],
    planExpirationDays: {
      expiredPlanThreshold: 0,
      expiringSoonPlanThreshold: 5,
      trialPeriodDuration: TRIAL_DETAILS.PERIOD_IN_DAYS,
    },
  }),

  getters: {
    getSpaceTags: (state: State): Tag[] => state.tags,
    getSpaces: (state: State): LimitedSpace[] => state.euSpaces,
    getSpacesFromRegion:
      (state: State) =>
      (region?: Region | null): LimitedSpace[] => {
        if (region) {
          return state[`${region}Spaces`]
        }

        return state.euSpaces
      },
    getSpaceNameById:
      (state: State) =>
      (id: number): string | null => {
        const selectedSpace = state.euSpaces.find((space) => space.id === id)
        return selectedSpace ? selectedSpace.name : null
      },
    getPlanExpirationDays: (state: State): PlanExpiration =>
      state.planExpirationDays,
    shouldLoadSpacesFromOtherRegions: (): boolean =>
      !config.data.ignoringRegions() &&
      !config.data.isBringYourOwnCloud() &&
      !config.data.isStagingDevEnv(),
  },

  actions: {
    clearSpaces(): void {
      this.euSpaces = []
      this.usSpaces = []
      this.cnSpaces = []
      this.caSpaces = []
      this.apSpaces = []
    },
    setSpaceTags(tags): void {
      this.tags = tags
    },
    setSpaces({
      region,
      spaces,
    }: {
      region: Region | null
      spaces: LimitedSpace[]
    }): void {
      if (region) {
        this[`${region}Spaces`] = spaces
      } else {
        this.euSpaces = spaces
      }
    },

    async loadSpaceTags(spaceId: string | number): Promise<void> {
      const { data } = await client.get(`spaces/${spaceId}/tags`, {
        region: useSpace().region,
      })
      return this.setSpaceTags(data.tags)
    },

    async loadSpaces(region: Region | null = null) {
      const response = await handleRequest({
        apiCall: (): Promise<AxiosResponse<{ spaces: LimitedSpace[] }>> =>
          client.get('spaces', { region }),
        expectedErrors: new Map([[404, t('No spaces found')]]),
      })

      if (response.status === 'success') {
        this.setSpaces({ region, spaces: response.data.spaces })
      }

      return response
    },

    async loadAllCollaboratorsBySpaceIds(payload: {
      spaceIds: number[]
      spaceId: number
      region: Region | null
      actAsRoles?: boolean
    }): Promise<Collaborator[]> {
      const { spaceIds, spaceId, region = null, actAsRoles = true } = payload

      const actAsRolesParam = actAsRoles ? '&with_act_as_roles=true' : ''
      const { data } = await client.get(
        `spaces/${spaceId}/collaborators?by_space_ids=${spaceIds}${actAsRolesParam}`,
        { region },
      )
      return data.collaborators
    },
  },
})

export default useSpaces
